import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { apiService } from '../services/api';
import { ProjectSchedule, WorkflowTask } from '../types';
import { PhaseCard } from '../components/ProjectSchedule/PhaseCard';
import { TaskStartDialog } from '../components/ProjectSchedule/TaskStartDialog';
import { ProjectResetDialog } from '../components/ProjectSchedule/ProjectResetDialog';
import { getUserAccessToken } from '../utils/tokenStorage';

interface ProjectSchedulePageProps {
  projectId?: number;
}

export const ProjectSchedulePage = ({ projectId: propProjectId }: ProjectSchedulePageProps) => {
  const params = useParams<{ projectId?: string }>();
  const projectId = propProjectId || (params.projectId ? parseInt(params.projectId, 10) : 1);

  const [schedule, setSchedule] = useState<ProjectSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<WorkflowTask | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadSchedule();
  }, [projectId]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      setWarning(null);
      setSuccess(null);
      const data = await apiService.getProjectSchedule(projectId);
      
      // Validate schedule data structure
      if (!data) {
        throw new Error('No schedule data received from server');
      }
      
      if (!data.phases || data.phases.length === 0) {
        throw new Error('Schedule data is incomplete: No phases found. The project may need to be re-seeded.');
      }
      
      if (data.currentPhaseId === undefined || data.currentPhaseId === null) {
        throw new Error('Schedule data is incomplete: currentPhaseId is missing. The project may need to be re-seeded.');
      }
      
      // Check if currentPhaseId matches any phase
      const currentPhaseExists = data.phases.some(p => p.phaseId === data.currentPhaseId);
      if (!currentPhaseExists) {
        console.warn('Warning: currentPhaseId does not match any phase. currentPhaseId:', data.currentPhaseId, 'Available phaseIds:', data.phases.map(p => p.phaseId));
      }
      
      // Validate each phase has tasks
      const phasesWithoutTasks = data.phases.filter(p => !p.tasks || p.tasks.length === 0);
      if (phasesWithoutTasks.length > 0) {
        console.warn('Warning: Some phases have no tasks:', phasesWithoutTasks.map(p => p.name));
      }
      
      setSchedule(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load project schedule';
      setError(errorMessage);
      console.error('Error loading schedule:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleStartTask = (task: WorkflowTask) => {
    setSelectedTask(task);
  };

  const handleTaskStart = async (
    taskId: number,
    fileData: { siteId: string; driveId: string; itemId: string },
    emails: { revisionEmail?: string; approvalEmail?: string; reviewEmail?: string }
  ) => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Find the phaseId for this task
      let phaseId: number | undefined;
      if (schedule) {
        for (const phase of schedule.phases) {
          const task = phase.tasks.find(t => t.taskId === taskId);
          if (task) {
            phaseId = phase.phaseId;
            break;
          }
        }
      }
      
      if (!phaseId) {
        throw new Error('Unable to determine phase for this task');
      }
      
      // Get user access token from storage
      const userAccessToken = getUserAccessToken();
      
      if (!userAccessToken) {
        console.warn('No user access token found. Email sending may fail.');
      }
      
      const taskStartResponse = await apiService.startTask(taskId, projectId, phaseId, fileData, emails, userAccessToken);
      
      // Handle email sending warnings/errors from backend
      setError(null);
      setWarning(null);
      setSuccess(null);
      
      if (taskStartResponse.warning) {
        console.warn('Backend warning:', taskStartResponse.warning);
        setWarning(taskStartResponse.warning);
      }
      
      if (taskStartResponse.emailSent === false) {
        const emailErrorMessage = taskStartResponse.emailError || 'Email failed to send';
        console.error('Email sending failed:', emailErrorMessage);
        // Show warning but don't block the workflow - task was created successfully
        setWarning(`Task started successfully, but email failed to send: ${emailErrorMessage}`);
      } else if (taskStartResponse.emailSent === true) {
        setSuccess('Task started and email sent successfully');
      } else if (taskStartResponse.success) {
        // Task started successfully but email status unknown
        setSuccess('Task started successfully');
      }
      
      await loadSchedule(); // Refresh schedule
      setSelectedTask(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start task';
      setError(errorMessage);
      setRefreshing(false);
      console.error('Error starting task:', err);
    }
  };

  const handleTaskViewDetails = (task: WorkflowTask) => {
    // TODO: Implement task details view
    // For now, just show an alert
    alert(`Task Details:\nName: ${task.name}\nStatus: ${task.taskInstance?.status || task.status}\nType: ${task.taskType}`);
  };

  const handleResetProject = () => {
    setResetDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    try {
      setRefreshing(true);
      setError(null);
      setWarning(null);
      setSuccess(null);
      await apiService.resetProject(projectId);
      await loadSchedule(); // Refresh schedule
      setResetDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset project');
      setRefreshing(false);
      console.error('Error resetting project:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETE': return 'success';
      case 'CANCELLED': return 'error';
      case 'IN_PROGRESS': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !schedule) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!schedule) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No schedule data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" color="inherit" onClick={handleResetProject} disabled={refreshing}>
            Reset Project
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
            {warning}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {refreshing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            {schedule.projectName}
          </Typography>
          <Chip
            label={schedule.status}
            color={getStatusColor(schedule.status) as 'success' | 'error' | 'info' | 'default'}
            size="small"
          />
        </Box>

        {schedule.phases.map((phase) => {
          // Phase is active if it matches currentPhaseId
          const isActive = phase.phaseId === schedule.currentPhaseId;
          
          // Phase is locked only if it's not the active phase and hasn't been started
          // Active phases should never be locked, allowing their tasks to be started
          const isLocked = !isActive && phase.status === 'NOT_STARTED';
          
          
          return (
            <PhaseCard
              key={phase.phaseId}
              phase={phase}
              isActive={isActive}
              isLocked={isLocked}
              onTaskStart={handleStartTask}
              onTaskViewDetails={handleTaskViewDetails}
            />
          );
        })}
      </Box>

      {selectedTask && (
        <TaskStartDialog
          open={!!selectedTask}
          task={selectedTask as any}
          onClose={() => setSelectedTask(null)}
          onStart={handleTaskStart}
        />
      )}

      <ProjectResetDialog
        open={resetDialogOpen}
        projectName={schedule.projectName}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleConfirmReset}
      />
    </Box>
  );
};

export default ProjectSchedulePage;

